import { useEffect, useState } from "react";
import { API_BASE } from "@/lib/api";
import api, { formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileSpreadsheet, BarChart3, Plus, Trash2, Landmark, Printer } from "lucide-react";
import { toast } from "sonner";

function download(url) {
  const token = localStorage.getItem("token");
  fetch(url, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
    .then(async (r) => {
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const disp = r.headers.get("Content-Disposition") || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : "reporte.xlsx";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    })
    .catch(() => toast.error("No se pudo descargar el reporte"));
}

const todayISO = () => new Date().toISOString().slice(0, 10);
const firstOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const COL_LABELS = {
  numero_pedido: "Pedido",
  numero_factura: "N° Factura",
  cliente: "Cliente",
  banco: "Banco",
  metodo_pago: "Método",
  monto: "Monto",
  total_pedido: "Total pedido",
  saldo_pendiente: "Saldo",
};

function PreviewTable({ rows, cols }) {
  if (!rows || rows.length === 0) return <div className="text-xs text-zinc-400 py-3">Sin datos.</div>;
  return (
    <div className="overflow-x-auto border border-zinc-200 rounded-md max-h-96 overflow-y-auto">
      <table className="w-full text-xs" data-testid="preview-table">
        <thead className="bg-zinc-50 sticky top-0">
          <tr className="text-left">
            {cols.map((c) => <th key={c} className="px-3 py-2 uppercase tracking-widest font-semibold text-zinc-500 text-[10px]">{COL_LABELS[c] || c}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.slice(0, 500).map((r, i) => (
            <tr key={i} className="hover:bg-zinc-50">
              {cols.map((c) => (
                <td key={c} className="px-3 py-1.5 tabular-nums">
                  {typeof r[c] === "number" ? Number(r[c]).toFixed(2) : (r[c] || "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 500 && <div className="text-[10px] text-zinc-400 px-3 py-1">Mostrando 500 de {rows.length}. Descarga Excel para el total.</div>}
    </div>
  );
}

function RangeBar({ desde, hasta, setDesde, setHasta, onExport, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
      <div>
        <Label className="text-xs uppercase tracking-widest">Desde</Label>
        <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid={`${testidPrefix}-desde`} />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest">Hasta</Label>
        <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid={`${testidPrefix}-hasta`} />
      </div>
      <Button
        variant="outline" size="sm"
        onClick={() => { const t = todayISO(); setDesde(t); setHasta(t); }}
        data-testid={`${testidPrefix}-hoy`}
      >Hoy</Button>
      <Button
        variant="outline" size="sm"
        onClick={() => { setDesde(firstOfMonthISO()); setHasta(todayISO()); }}
        data-testid={`${testidPrefix}-mes`}
      >Mes</Button>
      <Button
        variant="outline"
        className="gap-2 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
        onClick={onExport}
        data-testid={`${testidPrefix}-xlsx-btn`}
      >
        <FileSpreadsheet className="w-4 h-4" /> Descargar Excel
      </Button>
    </div>
  );
}

export default function Reportes() {
  // Ranges (per tab)
  const [factDesde, setFactDesde] = useState(firstOfMonthISO());
  const [factHasta, setFactHasta] = useState(todayISO());
  const [pendDesde, setPendDesde] = useState("");
  const [pendHasta, setPendHasta] = useState("");
  const [hoyDesde, setHoyDesde] = useState(todayISO());
  const [hoyHasta, setHoyHasta] = useState(todayISO());
  const [ciudadDesde, setCiudadDesde] = useState(firstOfMonthISO());
  const [ciudadHasta, setCiudadHasta] = useState(todayISO());
  const [dptoDesde, setDptoDesde] = useState(firstOfMonthISO());
  const [dptoHasta, setDptoHasta] = useState(todayISO());
  const [cierreFecha, setCierreFecha] = useState(todayISO());
  const [resDesde, setResDesde] = useState(firstOfMonthISO());
  const [resHasta, setResHasta] = useState(todayISO());
  const [bancosDesde, setBancosDesde] = useState(firstOfMonthISO());
  const [bancosHasta, setBancosHasta] = useState(todayISO());

  // Bancos live view
  const [bancos, setBancos] = useState([]);
  const [nuevoBanco, setNuevoBanco] = useState("");
  const [nuevoTipo, setNuevoTipo] = useState("banco");
  const [disp, setDisp] = useState({ items: [], total: 0 });

  // Preview data per tab
  const [previews, setPreviews] = useState({});
  const fetchPreview = async (key, path, params) => {
    try {
      const { data } = await api.get(path, { params });
      setPreviews((p) => ({ ...p, [key]: data }));
    } catch (e) {
      toast.error("No se pudo cargar la vista previa");
    }
  };

  const loadBancos = async () => {
    const { data } = await api.get("/bancos");
    setBancos(data);
  };
  const loadDisp = async () => {
    try {
      const { data } = await api.get("/reports/disponibilidad-bancos", {
        params: { desde: bancosDesde, hasta: bancosHasta },
      });
      setDisp(data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadBancos(); }, []);
  useEffect(() => { loadDisp(); }, [bancosDesde, bancosHasta]);

  const addBanco = async () => {
    if (!nuevoBanco.trim()) return toast.error("Escribe un nombre");
    try {
      await api.post("/bancos", { nombre: nuevoBanco.trim(), tipo: nuevoTipo, activo: true });
      setNuevoBanco("");
      toast.success("Banco agregado");
      loadBancos();
      loadDisp();
    } catch (e) { toast.error("No se pudo crear"); }
  };
  const removeBanco = async (id) => {
    if (!window.confirm("¿Eliminar este banco?")) return;
    await api.delete(`/bancos/${id}`);
    loadBancos();
    loadDisp();
  };

  const buildUrl = (path, params) => {
    const p = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => v && p.append(k, v));
    return `${API_BASE}${path}?${p.toString()}`;
  };

  return (
    <div className="space-y-6" data-testid="reportes-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Análisis</div>
        <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
          <BarChart3 className="w-8 h-8" /> Reportes
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">Reportes contables y operativos con rango de fechas.</p>
      </header>

      <Tabs defaultValue="facturacion">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="facturacion" data-testid="tab-fact">Facturación mensual</TabsTrigger>
          <TabsTrigger value="facturacion-hoy" data-testid="tab-fact-hoy">Facturación de hoy</TabsTrigger>
          <TabsTrigger value="pendientes" data-testid="tab-pend">Pendientes de facturar</TabsTrigger>
          <TabsTrigger value="ciudad" data-testid="tab-ciudad">Entregas Ciudad</TabsTrigger>
          <TabsTrigger value="dpto" data-testid="tab-dpto">Entregas Departamento</TabsTrigger>
          <TabsTrigger value="cierre" data-testid="tab-cierre">Cierre diario</TabsTrigger>
          <TabsTrigger value="resumen" data-testid="tab-resumen">Resumen ejecutivo</TabsTrigger>
          <TabsTrigger value="bancos" data-testid="tab-bancos">Disponibilidad bancos</TabsTrigger>
        </TabsList>

        {/* Facturación mensual (pagados) */}
        <TabsContent value="facturacion" className="mt-6">
          <Card className="p-6 border-zinc-200 rounded-md max-w-3xl">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Facturación</div>
            <div className="text-xl font-semibold mt-0.5 mb-4">Pagos cobrados en el rango</div>
            <RangeBar
              desde={factDesde} hasta={factHasta}
              setDesde={setFactDesde} setHasta={setFactHasta}
              onExport={() => {
                const month = factDesde ? factDesde.slice(0, 7) : "";
                download(buildUrl("/reports/facturacion.xlsx", { month }));
              }}
              testidPrefix="fact"
            />
            <div className="mt-3">
              <Button variant="outline" size="sm" onClick={() => fetchPreview("mensual", "/reports/preview/mensual", { month: (factDesde || todayISO()).slice(0, 7) })} data-testid="fact-preview-btn">Ver datos</Button>
            </div>
            {previews.mensual && (
              <div className="mt-4">
                <div className="mb-2 font-semibold">Total {previews.mensual.month}: <span className="text-emerald-700">{formatMoney(previews.mensual.total)}</span> ({previews.mensual.count} facturas)</div>
                <PreviewTable rows={previews.mensual.items} cols={["numero_pedido", "numero_factura", "cliente", "banco", "metodo_pago", "monto"]} />
              </div>
            )}
            <p className="text-xs text-zinc-500 mt-4">Pagos cobrados en el mes.</p>
          </Card>
        </TabsContent>

        {/* Facturación hoy */}
        <TabsContent value="facturacion-hoy" className="mt-6 space-y-4">
          <Card className="p-6 border-zinc-200 rounded-md">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Cobro del día</div>
            <div className="text-xl font-semibold mt-0.5 mb-4">Facturación de hoy o rango</div>
            <RangeBar
              desde={hoyDesde} hasta={hoyHasta}
              setDesde={setHoyDesde} setHasta={setHoyHasta}
              onExport={() => download(buildUrl("/reports/facturacion-hoy.xlsx", { desde: hoyDesde, hasta: hoyHasta }))}
              testidPrefix="hoy"
            />
            <div className="flex gap-2 mt-3 flex-wrap">
              <Button
                variant="outline" size="sm"
                onClick={() => { const d = todayISO(); setHoyDesde(d); setHoyHasta(d); }}
                data-testid="hoy-preset-hoy"
              >Hoy</Button>
              <Button variant="outline" size="sm" onClick={() => fetchPreview("hoy", "/reports/preview/facturacion-hoy")} data-testid="hoy-preview-btn">Ver datos</Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                onClick={() => download(`${API_BASE}/reports/facturacion-hoy.xlsx`)}
                data-testid="hoy-resumen-btn"
              >
                <FileSpreadsheet className="w-4 h-4" /> Resumen Hoy
              </Button>
            </div>
            {previews.hoy && (
              <div className="mt-4">
                <div className="mb-2 font-semibold">Total facturado hoy: <span className="text-emerald-700">{formatMoney(previews.hoy.total)}</span> ({previews.hoy.count} facturas)</div>
                <PreviewTable rows={previews.hoy.items} cols={["numero_pedido", "numero_factura", "cliente", "banco", "metodo_pago", "monto"]} />
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Pendientes de facturar */}
        <TabsContent value="pendientes" className="mt-6">
          <Card className="p-6 border-zinc-200 rounded-md max-w-3xl">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Cobranza</div>
            <div className="text-xl font-semibold mt-0.5 mb-4">Pedidos pendientes de facturar y con falta de pago</div>
            <RangeBar
              desde={pendDesde} hasta={pendHasta}
              setDesde={setPendDesde} setHasta={setPendHasta}
              onExport={() => download(buildUrl("/reports/pendientes-facturar.xlsx", { desde: pendDesde, hasta: pendHasta }))}
              testidPrefix="pend"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => fetchPreview("pend", "/reports/preview/pendientes")} data-testid="pend-preview-btn">Ver datos</Button>
              <Button
                size="sm"
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white gap-2"
                onClick={() => download(buildUrl("/reports/pendientes-facturar.xlsx", { desde: pendDesde, hasta: pendHasta, tipo: "ciudad" }))}
                data-testid="pend-export-ciudad-btn"
              >
                Exportar Ciudad
              </Button>
              <Button
                size="sm"
                className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white gap-2"
                onClick={() => download(buildUrl("/reports/pendientes-facturar.xlsx", { desde: pendDesde, hasta: pendHasta, tipo: "departamento" }))}
                data-testid="pend-export-dpto-btn"
              >
                Exportar Departamento
              </Button>
            </div>
            <div className="mt-2 text-[11px] text-zinc-500">
              El botón "Exportar" arriba exporta todos los pendientes. Usa los botones de abajo para separar por Ciudad o Departamento.
            </div>
            {previews.pend && (
              <div className="mt-4">
                <div className="mb-2 font-semibold">Saldo total pendiente: <span className="text-orange-700">{formatMoney(previews.pend.saldo_total)}</span> ({previews.pend.count} pedidos)</div>
                <PreviewTable rows={previews.pend.items.map((o) => ({
                  numero_pedido: o.numero,
                  cliente: o.cliente_nombre,
                  total_pedido: o.total,
                  saldo_pendiente: o.saldo_pendiente ?? Math.max(0, (o.total || 0) - (o.monto_facturado_acum || 0)),
                }))} cols={["numero_pedido", "cliente", "total_pedido", "saldo_pendiente"]} />
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Entregas Ciudad */}
        <TabsContent value="ciudad" className="mt-6">
          <Card className="p-6 border-zinc-200 rounded-md max-w-3xl">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Distribución</div>
            <div className="text-xl font-semibold mt-0.5 mb-4">Entregas Ciudad</div>
            <RangeBar
              desde={ciudadDesde} hasta={ciudadHasta}
              setDesde={setCiudadDesde} setHasta={setCiudadHasta}
              onExport={() => {
                const month = ciudadDesde ? ciudadDesde.slice(0, 7) : "";
                download(buildUrl("/reports/entregas-piloto.xlsx", { month }));
              }}
              testidPrefix="ciudad"
            />
          </Card>
        </TabsContent>

        {/* Entregas Departamento */}
        <TabsContent value="dpto" className="mt-6">
          <Card className="p-6 border-zinc-200 rounded-md max-w-3xl">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Distribución</div>
            <div className="text-xl font-semibold mt-0.5 mb-4">Envíos a Departamento (agencia)</div>
            <RangeBar
              desde={dptoDesde} hasta={dptoHasta}
              setDesde={setDptoDesde} setHasta={setDptoHasta}
              onExport={() => {
                const month = dptoDesde ? dptoDesde.slice(0, 7) : "";
                download(buildUrl("/reports/envios-departamento.xlsx", { month }));
              }}
              testidPrefix="dpto"
            />
          </Card>
        </TabsContent>

        {/* Cierre diario */}
        <TabsContent value="cierre" className="mt-6 space-y-4">
          <Card className="p-6 border-zinc-200 rounded-md">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Operativo</div>
            <div className="text-xl font-semibold mt-0.5 mb-4">Cierre diario</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-xs uppercase tracking-widest">Fecha</Label>
                <Input type="date" value={cierreFecha} onChange={(e) => setCierreFecha(e.target.value)} data-testid="cierre-fecha" />
              </div>
              <Button variant="outline" onClick={() => fetchPreview("cierre", "/reports/preview/cierre", { fecha: cierreFecha })} data-testid="cierre-preview-btn">Ver datos</Button>
              <Button variant="outline" className="gap-2 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                onClick={() => download(buildUrl("/reports/cierre-diario.xlsx", { fecha: cierreFecha }))}
                data-testid="cierre-xlsx-btn">
                <FileSpreadsheet className="w-4 h-4" /> Descargar Excel
              </Button>
            </div>
            {previews.cierre && (
              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Card className="p-4 border-emerald-200 bg-emerald-50/40">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Ingresos del día</div>
                    <div className="text-3xl font-manrope font-black text-emerald-900" data-testid="cierre-total-dia">{formatMoney(previews.cierre.day.total)}</div>
                    <div className="text-xs text-emerald-700 mt-1">{previews.cierre.day.count} facturas</div>
                    <div className="mt-3 space-y-1">
                      {previews.cierre.day.bancos.map((b) => (
                        <div key={b.nombre} className="flex justify-between text-sm border-b border-emerald-100 py-1">
                          <span>{b.nombre}</span>
                          <span className="font-semibold tabular-nums">{formatMoney(b.monto)}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                  <Card className="p-4 border-indigo-200 bg-indigo-50/40">
                    <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-semibold">Acumulado mensual (incluye día)</div>
                    <div className="text-3xl font-manrope font-black text-indigo-900" data-testid="cierre-total-mes">{formatMoney(previews.cierre.month.total)}</div>
                    <div className="text-xs text-indigo-700 mt-1">Desde {previews.cierre.month_from}</div>
                    <div className="mt-3 space-y-1">
                      {previews.cierre.month.bancos.map((b) => (
                        <div key={b.nombre} className="flex justify-between text-sm border-b border-indigo-100 py-1">
                          <span>{b.nombre}</span>
                          <span className="font-semibold tabular-nums">{formatMoney(b.monto)}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
                <div>
                  <div className="font-semibold text-sm mb-2">Detalle del día</div>
                  <PreviewTable rows={previews.cierre.day.items} cols={["numero_pedido", "numero_factura", "cliente", "banco", "monto", "total_pedido"]} />
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Resumen ejecutivo */}
        <TabsContent value="resumen" className="mt-6">
          <Card className="p-6 border-zinc-200 rounded-md max-w-3xl">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Gerencial</div>
            <div className="text-xl font-semibold mt-0.5 mb-4">Resumen ejecutivo</div>
            <RangeBar
              desde={resDesde} hasta={resHasta}
              setDesde={setResDesde} setHasta={setResHasta}
              onExport={() => download(buildUrl("/reports/resumen-ejecutivo.xlsx", { desde: resDesde, hasta: resHasta }))}
              testidPrefix="resumen"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  const token = localStorage.getItem("token");
                  const params = new URLSearchParams({ desde: resDesde || "", hasta: resHasta || "" });
                  // Abre en nueva ventana con Authorization header — usa fetch + blob para poder incluir el header
                  fetch(`${API_BASE}/reports/resumen-ejecutivo.pdf?${params.toString()}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                  }).then((r) => r.blob()).then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const w = window.open(url, "_blank");
                    if (w) setTimeout(() => { try { w.print(); } catch {} }, 800);
                  });
                }}
                variant="outline"
                className="gap-2"
                data-testid="resumen-print-btn"
              >
                <Printer className="w-4 h-4" /> Imprimir PDF
              </Button>
            </div>
            <p className="text-xs text-zinc-500 mt-4">Incluye KPIs, ranking de vendedores, top productos, <b>gastos por categoría</b> y utilidad neta (Ventas − Gastos).</p>
          </Card>
        </TabsContent>

        {/* Disponibilidad de bancos */}
        <TabsContent value="bancos" className="mt-6 space-y-4">
          <Card className="p-6 border-zinc-200 rounded-md">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Métodos de pago</div>
            <div className="text-xl font-semibold mt-0.5 mb-4 flex items-center gap-2">
              <Landmark className="w-5 h-5" /> Bancos y efectivo
            </div>
            <div className="flex flex-wrap items-end gap-2 mb-4">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs uppercase tracking-widest">Nuevo</Label>
                <Input value={nuevoBanco} onChange={(e) => setNuevoBanco(e.target.value)}
                       placeholder="Ej. Banrural, BAM, BI, Efectivo…" data-testid="banco-nombre" />
              </div>
              <select
                value={nuevoTipo}
                onChange={(e) => setNuevoTipo(e.target.value)}
                className="h-10 border border-zinc-200 rounded-md px-3 text-sm"
                data-testid="banco-tipo"
              >
                <option value="banco">Banco</option>
                <option value="efectivo">Efectivo</option>
              </select>
              <Button className="bg-[#002FA7] hover:bg-[#00227A] gap-2" onClick={addBanco} data-testid="banco-add">
                <Plus className="w-4 h-4" /> Agregar
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {bancos.length === 0 && <div className="text-xs text-zinc-400">Aún no hay bancos creados.</div>}
              {bancos.map((b) => (
                <div key={b.id} className="flex items-center justify-between border border-zinc-200 rounded-md px-3 py-2 text-sm">
                  <div>
                    <div className="font-semibold">{b.nombre}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{b.tipo}</div>
                  </div>
                  <button onClick={() => removeBanco(b.id)} className="text-red-600 hover:bg-red-50 rounded p-1" data-testid={`banco-del-${b.nombre}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 border-zinc-200 rounded-md">
            <div className="text-xl font-semibold mb-4">Disponibilidad — pagos recibidos</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Desde</Label>
                <Input type="date" value={bancosDesde} onChange={(e) => setBancosDesde(e.target.value)} data-testid="bancos-desde" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Hasta</Label>
                <Input type="date" value={bancosHasta} onChange={(e) => setBancosHasta(e.target.value)} data-testid="bancos-hasta" />
              </div>
              <Button
                variant="outline" className="gap-2 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                onClick={() => download(buildUrl("/reports/disponibilidad-bancos.xlsx", { desde: bancosDesde, hasta: bancosHasta }))}
                data-testid="bancos-xlsx-btn"
              >
                <FileSpreadsheet className="w-4 h-4" /> Descargar Excel
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600">
                  <tr className="text-left">
                    <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold">Banco / Método</th>
                    <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold">Tipo</th>
                    <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-right">N° pagos</th>
                    <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-right">Monto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100" data-testid="bancos-table">
                  {disp.items.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-zinc-400 py-6">Sin movimientos.</td></tr>
                  )}
                  {disp.items.map((b, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-semibold">{b.nombre}</td>
                      <td className="px-3 py-2 text-zinc-500 text-xs uppercase tracking-widest">{b.tipo}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{b.count}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatMoney(b.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-100 font-semibold">
                    <td colSpan={3} className="px-3 py-3 text-right uppercase tracking-widest text-xs">Total disponible</td>
                    <td className="px-3 py-3 text-right tabular-nums text-emerald-700 text-lg" data-testid="bancos-total">
                      {formatMoney(disp.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
